import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguageContext } from '../../context/language_context';
import axios from 'axios';
import { categories_url } from '../../utils/constants';
import { getSecureUrl } from '../../utils/helpers';
import daristaGoldLogo from '../../assets/daristaGoldLogoPM.png';

// Accordion component for mobile footer sections
const FooterAccordion = ({ title, children, isArabic }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="accordion-section">
      <button 
        className="accordion-header" 
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h4 className="accordion-title">{title}</h4>
        <motion.span
          className="accordion-icon"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FaChevronDown />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className="accordion-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Footer = () => {
  const { isArabic } = useLanguageContext();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(categories_url);
        const cats = (response.data.data || []).map((cat) => ({
          ...cat,
          image: cat.image ? { ...cat.image, url: getSecureUrl(cat.image.url) } : cat.image,
        }));
        setCategories(cats);
      } catch (err) {
        // Silent fail
      }
    };
    fetchCategories();
  }, []);

  // Build shop links from categories only
  const shopLinks = categories.map((cat) => ({
    to: `/products?category=${encodeURIComponent(cat.name)}`,
    label: cat.name.charAt(0).toUpperCase() + cat.name.slice(1),
  }));

  const supportLinks = [
    { to: '/track-order', label: isArabic ? 'تتبع الطلب' : 'Track Order' },
    { to: '/faq', label: isArabic ? 'الأسئلة الشائعة' : 'FAQ' },
    { to: '/shipping', label: isArabic ? 'الشحن والتوصيل' : 'Shipping & Delivery' },
    { to: '/returns', label: isArabic ? 'سياسة الإرجاع' : 'Return Policy' },
  ];

  const companyLinks = [
    { to: '/about', label: isArabic ? 'من نحن' : 'About Darista' },
    { to: '/our-story', label: isArabic ? 'قصتنا' : 'Our Story' },
    { to: '/contact', label: isArabic ? 'اتصل بنا' : 'Contact Us' },
  ];

  const renderLinks = (links) => (
    <ul className="col-links">
      {links.map((link, idx) => (
        <li key={idx}><Link to={link.to}>{link.label}</Link></li>
      ))}
    </ul>
  );

  return (
    <Wrapper dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="footer-container">
        <div className="footer-grid">
          {/* Brand + Contact */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img src={daristaGoldLogo} alt="Darista" style={{ height: '120px', width: 'auto' }} />
            </Link>
            <p className="footer-tagline">
              {isArabic
                ? 'حلول ذكية لمنزلك. كل ما تحتاجه لجعل حياتك اليومية أسهل وأجمل.'
                : 'Smart solutions for your home. Everything you need to make your daily life easier and more beautiful.'}
            </p>
            <div className="footer-contact">
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <span>{isArabic ? 'تونس' : 'Tunisia'}</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <a href="tel:+21624967414">+216 24 967 414</a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <a href="mailto:contact@darista.tn">contact@darista.tn</a>
              </div>
            </div>
            <div className="footer-social">
              <a href="https://www.facebook.com/daristaTN/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook"><FaFacebookF /></a>
              <a href="https://www.instagram.com/daristatn?igsh=MTA1anAya3ZqcnFydg==" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram"><FaInstagram /></a>
            </div>
          </div>

          {/* Desktop columns */}
          <div className="footer-col desktop-only">
            <h4 className="col-title">{isArabic ? 'تسوق' : 'Shop'}</h4>
            {renderLinks(shopLinks)}
          </div>

          <div className="footer-col desktop-only">
            <h4 className="col-title">{isArabic ? 'الدعم' : 'Support'}</h4>
            {renderLinks(supportLinks)}
          </div>

          <div className="footer-col desktop-only">
            <h4 className="col-title">{isArabic ? 'الشركة' : 'Company'}</h4>
            {renderLinks(companyLinks)}
          </div>

          {/* Mobile accordions */}
          <div className="footer-accordions mobile-only">
            <FooterAccordion title={isArabic ? 'تسوق' : 'SHOP'} isArabic={isArabic}>
              {renderLinks(shopLinks)}
            </FooterAccordion>
            <FooterAccordion title={isArabic ? 'الدعم' : 'SUPPORT'} isArabic={isArabic}>
              {renderLinks(supportLinks)}
            </FooterAccordion>
            <FooterAccordion title={isArabic ? 'الشركة' : 'COMPANY'} isArabic={isArabic}>
              {renderLinks(companyLinks)}
            </FooterAccordion>
          </div>
        </div>

        {/* Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-inner">
            <p className="copyright">
              © {new Date().getFullYear()} Darista. {isArabic ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </p>
            <div className="trust-badges">
              <div className="trust-badge">
                <span className="badge-dot"></span>
                <span>{isArabic ? 'الدفع عند الاستلام' : 'Cash on Delivery'}</span>
              </div>
              <div className="trust-badge">
                <span className="badge-dot"></span>
                <span>{isArabic ? 'دفع آمن' : 'Secure Checkout'}</span>
              </div>
              <div className="trust-badge">
                <span className="badge-dot"></span>
                <span>{isArabic ? 'توصيل لتونس' : 'Tunisia Delivery'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default Footer;

const Wrapper = styled.footer`
  background: var(--clr-charcoal);
  color: rgba(255, 255, 255, 0.8);
  padding-top: 3rem;

  .footer-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .footer-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 3rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  /* Show/hide based on viewport */
  .desktop-only {
    display: block;
  }
  .mobile-only {
    display: none;
  }

  .footer-brand {
    .footer-logo {
      font-family: var(--font-display);
      font-size: 1.75rem;
      font-weight: 700;
      color: #fff;
      text-decoration: none;
      display: inline-block;
      margin-bottom: 1rem;
    }
    .footer-tagline {
      font-family: var(--font-body);
      font-size: 0.85rem;
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.6);
      margin-bottom: 1rem;
      max-width: 350px;
    }
  }

  .footer-contact {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .contact-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-family: var(--font-body);
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    .contact-icon { font-size: 0.9rem; }
    a, span {
      color: rgba(255, 255, 255, 0.6);
      text-decoration: none;
      transition: color 0.2s ease;
      direction: ltr;
      unicode-bidi: plaintext;
      &:hover { color: var(--clr-gold); }
    }
  }

  .footer-social {
    display: flex;
    gap: 0.5rem;
  }
  .social-link {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    text-decoration: none;
    transition: all 0.3s ease;
    &:hover {
      background: var(--clr-olive);
      color: #fff;
    }
  }

  .footer-col {
    .col-title {
      font-family: var(--font-body);
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--clr-gold);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 1rem;
    }
    .col-links {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      li a {
        font-family: var(--font-body);
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.6);
        text-decoration: none;
        transition: all 0.2s ease;
        &:hover {
          color: #fff;
        }
      }
    }
  }

  /* Footer bottom */
  .footer-bottom {
    padding: 1.5rem 0;
  }
  .footer-bottom-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  .copyright {
    font-family: var(--font-body);
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.4);
    margin: 0;
    text-align: center;
  }
  .trust-badges {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    justify-content: center;
  }
  .trust-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: var(--font-body);
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.5);
  }
  .badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--clr-olive);
  }

  /* Accordion styles for mobile */
  .footer-accordions {
    grid-column: 1 / -1;
  }
  .accordion-section {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  .accordion-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 0;
    background: none;
    border: none;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  .accordion-title {
    font-family: var(--font-body);
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--clr-gold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
  }
  .accordion-icon {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.75rem;
    display: flex;
    align-items: center;
  }
  .accordion-content {
    overflow: hidden;
    .col-links {
      padding-bottom: 1rem;
      li {
        padding: 0.4rem 0;
      }
      li a {
        display: block;
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.6);
        text-decoration: none;
        touch-action: manipulation;
        &:active {
          color: #fff;
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .footer-grid {
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
    }
  }

  @media (max-width: 768px) {
    padding-top: 2.5rem;

    .footer-grid {
      grid-template-columns: 1fr;
      gap: 0;
    }

    .desktop-only {
      display: none;
    }
    .mobile-only {
      display: block;
    }

    .footer-brand {
      text-align: left;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      margin-bottom: 0;

      .footer-logo {
        font-size: 1.5rem;
      }
      .footer-tagline {
        font-size: 0.75rem;
        max-width: 100%;
      }
    }

    .footer-contact {
      gap: 0.4rem;
    }
    .contact-item {
      font-size: 0.75rem;
    }

    .footer-social {
      margin-top: 0.5rem;
    }
    .social-link {
      width: 34px;
      height: 34px;
    }

    .footer-bottom {
      padding: 1.25rem 0;
    }
    .trust-badges {
      gap: 1rem;
    }
    .trust-badge {
      font-size: 0.65rem;
    }
    .badge-dot {
      width: 5px;
      height: 5px;
    }
    .copyright {
      font-size: 0.65rem;
    }
  }
`;